import type {
    Book,
    BookType,
    MergeTbaIntoBookRequest,
    MergeTbaIntoBookResponse,
    ResolveTbaBookRequest,
    ResolveTbaBookResponse,
} from "@book-club/shared";
import { isBookArchived } from "@book-club/shared";
import { limit, orderBy, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type { CreateBookInput, UpdateBookInput } from "@/features/books/types";
import { functionsService } from "@/infrastructure/firebase/firebase";
import firebaseDatabaseService from "@/infrastructure/firebase/firebaseDatabaseService";
import logger from "@/logger";

/**
 * Fetches all books from Firestore
 * @returns Array of all books (both scheduled and unscheduled)
 */
async function getAllBooks(): Promise<Book[]> {
    logger.debug("[Books] Fetching all books...");

    const books = await firebaseDatabaseService.getDocuments<Book>("books");

    logger.debug(`[Books] Found ${books.length} books`);

    return books;
}

/**
 * Fetches a single book by ID from Firestore
 * @param bookId - Book document ID to fetch
 * @returns Book object or null if not found
 */
async function getBookById(bookId: string): Promise<Book | null> {
    logger.debug(`[Books] Fetching book: ${bookId}`);

    const book = await firebaseDatabaseService.getDocument<Book>("books", bookId);

    if (!book) {
        logger.warn(`[Books] Book not found: ${bookId}`);
        return null;
    }

    logger.debug(`[Books] Found book: ${bookId}`);
    return book;
}

/**
 * Creates a new book via Cloud Function with server-side duplicate checking
 * @param bookData - Book data without id and createdAt
 * @returns Created book with id and createdAt
 */
async function createBook(bookData: CreateBookInput): Promise<Book> {
    const bookTitle =
        bookData.type === "specificBook"
            ? (bookData as { title?: string }).title
            : bookData.type === "authorFocus"
              ? "Author Focus"
              : "Free Choice";
    logger.debug("[Books] Creating book:", bookTitle);

    const createBookFunction = httpsCallable<CreateBookInput, Book>(functionsService, "createBook");
    const result = await createBookFunction(bookData);

    logger.debug(`[Books] Book created successfully: ${result.data.id}`);

    // Convert createdAt from ISO string to Date
    const book = result.data;
    return {
        ...book,
        createdAt: new Date(book.createdAt),
    };
}

/**
 * Updates an existing book via Cloud Function with server-side validation
 * @param bookId - Book document ID to update
 * @param bookData - Book data to update (all fields optional except type)
 * @returns Updated book
 */
async function updateBook(bookId: string, bookData: UpdateBookInput): Promise<Book> {
    logger.debug(`[Books] Updating book: ${bookId}`);

    const updateBookFunction = httpsCallable<{ bookId: string; bookData: UpdateBookInput }, Book>(
        functionsService,
        "updateBook"
    );
    const result = await updateBookFunction({ bookData, bookId });

    logger.debug(`[Books] Book updated successfully: ${bookId}`);

    // Convert createdAt from ISO string to Date
    const book = result.data;
    return {
        ...book,
        createdAt: new Date(book.createdAt),
    };
}

/**
 * Checks for duplicate book (client-side pre-check for UX)
 * This is non-authoritative - final check happens server-side
 * @param isbn - Optional ISBN to check
 * @param title - Title to check
 * @param author - Author to check
 * @returns Existing book if duplicate found, null otherwise
 */
async function checkDuplicateBook(isbn?: string, title?: string, author?: string): Promise<Book | null> {
    logger.debug("[Books] Checking for duplicate book:", { author, isbn, title });

    try {
        // Check by ISBN if provided
        if (isbn) {
            const booksByIsbn = await firebaseDatabaseService.getDocuments<Book>(
                "books",
                where("isbn", "==", isbn),
                where("isbn", "!=", null)
            );

            if (booksByIsbn.length > 0) {
                logger.debug(`[Books] Duplicate found by ISBN: ${isbn}`);
                return booksByIsbn[0];
            }
        }

        // Check by title and author if both provided
        if (title && author) {
            const booksByTitleAndAuthor = await firebaseDatabaseService.getDocuments<Book>(
                "books",
                where("title", "==", title.trim()),
                where("author", "==", author.trim())
            );

            if (booksByTitleAndAuthor.length > 0) {
                logger.debug(`[Books] Duplicate found by title/author: ${title} by ${author}`);
                return booksByTitleAndAuthor[0];
            }
        }

        logger.debug("[Books] No duplicate found");
        return null;
    } catch (error) {
        logger.error("[Books] Error checking for duplicate:", error);
        // Don't throw - this is just a UX check, server will do authoritative check
        return null;
    }
}

/**
 * Schedules a book for a specific month/year via Cloud Function
 * @param bookId - Book ID (null for session types)
 * @param bookType - Type of book to schedule
 * @param year - Year to schedule
 * @param month - Month to schedule (1-12)
 * @param sessionDates - Array of ISO datetime strings for session dates
 * @param author - Optional author (required for authorFocus type)
 * @param amazonUrl - Optional Amazon URL (for authorFocus type)
 * @param themeTitle - Optional theme title (for freeChoice type)
 * @returns Scheduled book
 */
async function scheduleBook(
    bookId: string | null,
    bookType: BookType,
    year: number,
    month: number,
    sessionDates: string[],
    author?: string,
    amazonUrl?: string,
    themeTitle?: string
): Promise<Book> {
    logger.debug(
        `[Books] Scheduling book: ${bookId ?? "new session"} for ${month}/${year} with ${sessionDates.length} sessions`
    );

    const scheduleBookFunction = httpsCallable<
        {
            amazonUrl?: string;
            author?: string;
            bookId: string | null;
            bookType: BookType;
            month: number;
            sessionDates: string[];
            themeTitle?: string;
            year: number;
        },
        Book
    >(functionsService, "scheduleBook");
    const result = await scheduleBookFunction({
        amazonUrl,
        author,
        bookId,
        bookType,
        month,
        sessionDates,
        ...(themeTitle !== undefined && themeTitle !== "" && { themeTitle: themeTitle.trim() }),
        year,
    });

    logger.debug(`[Books] Book scheduled successfully: ${result.data.id}`);

    // Convert createdAt from ISO string to Date
    const book = result.data;
    return book;
}

/**
 * Unschedules a book via Cloud Function
 * For specificBook: Removes year/month fields
 * For session types: Deletes the document
 * @param bookId - Book ID to unschedule
 * @returns Unscheduled book (or null if deleted)
 */
async function unscheduleBook(bookId: string): Promise<Book | null> {
    logger.debug(`[Books] Unscheduling book: ${bookId}`);

    const unscheduleBookFunction = httpsCallable<{ bookId: string }, Book | null>(functionsService, "unscheduleBook");
    const result = await unscheduleBookFunction({ bookId });

    logger.debug(`[Books] Book unscheduled successfully: ${bookId}`);

    if (!result.data) {
        return null; // Book was deleted (session type)
    }

    // Convert createdAt from ISO string to Date
    const book = result.data;
    return book;
}

async function resolveTbaBook(input: ResolveTbaBookRequest): Promise<ResolveTbaBookResponse> {
    logger.debug(`[Books] Resolving TBA book ${input.bookId} to "${input.title}"`);

    const resolveFn = httpsCallable<ResolveTbaBookRequest, ResolveTbaBookResponse>(functionsService, "resolveTbaBook");
    const result = await resolveFn(input);

    logger.debug(`[Books] TBA book resolved successfully: ${input.bookId}`);
    return result.data;
}

async function mergeTbaIntoBook(input: MergeTbaIntoBookRequest): Promise<MergeTbaIntoBookResponse> {
    logger.debug(`[Books] Merging TBA ${input.tbaBookId} into ${input.targetBookId}`);

    const mergeFn = httpsCallable<MergeTbaIntoBookRequest, MergeTbaIntoBookResponse>(
        functionsService,
        "mergeTbaIntoBook"
    );
    const result = await mergeFn(input);

    logger.debug(`[Books] TBA merged successfully into ${input.targetBookId}`);
    return result.data;
}

/**
 * Fetches the live (non-archived) book scheduled for a given year/month, if any.
 *
 * Uses limit(10) then filters for non-archived — same shape as the Cloud Function
 * write guards. A bare limit(1) with no order can return an archived same-month
 * leftover instead of the live TBA, which would mis-route merge-vs-close.
 */
async function getBookForMonth(year: number, month: number): Promise<Book | null> {
    logger.debug(`[Books] Fetching book for ${year}-${month}`);

    const books = await firebaseDatabaseService.getDocuments<Book>(
        "books",
        where("year", "==", year),
        where("month", "==", month),
        limit(10)
    );

    const book = books.find((candidate) => !isBookArchived(candidate)) ?? null;
    logger.debug(`[Books] Book for ${year}-${month}: ${book?.id ?? "none"}`);
    return book;
}

async function getVisibleBooks(): Promise<Book[]> {
    logger.debug("[Books] Fetching visible (non-archived) books...");
    const books = await firebaseDatabaseService.getDocuments<Book>("books", where("archivedAt", "==", null));
    logger.debug(`[Books] Found ${books.length} visible books`);
    return books;
}

async function getArchivedBooks(): Promise<Book[]> {
    logger.debug("[Books] Fetching archived books...");
    const books = await firebaseDatabaseService.getDocuments<Book>(
        "books",
        where("archivedAt", "!=", null),
        orderBy("archivedAt")
    );
    logger.debug(`[Books] Found ${books.length} archived books`);
    return books;
}

async function getArchivedBookCount(): Promise<number> {
    logger.debug("[Books] Fetching archived book count...");
    const count = await firebaseDatabaseService.getCount(
        "books",
        where("archivedAt", "!=", null),
        orderBy("archivedAt")
    );
    logger.debug(`[Books] Archived book count: ${count}`);
    return count;
}

async function archiveBook(bookId: string): Promise<void> {
    logger.debug(`[Books] Archiving book: ${bookId}`);

    const archiveBookFunction = httpsCallable<{ bookId: string }, { success: boolean }>(
        functionsService,
        "archiveBook"
    );
    await archiveBookFunction({ bookId });

    logger.debug(`[Books] Book archived successfully: ${bookId}`);
}

async function unarchiveBook(bookId: string): Promise<void> {
    logger.debug(`[Books] Unarchiving book: ${bookId}`);

    const unarchiveBookFunction = httpsCallable<{ bookId: string }, { success: boolean }>(
        functionsService,
        "unarchiveBook"
    );
    await unarchiveBookFunction({ bookId });

    logger.debug(`[Books] Book unarchived successfully: ${bookId}`);
}

const booksService = {
    archiveBook,
    checkDuplicateBook,
    createBook,
    getAllBooks,
    getArchivedBookCount,
    getArchivedBooks,
    getBookById,
    getBookForMonth,
    getVisibleBooks,
    mergeTbaIntoBook,
    resolveTbaBook,
    scheduleBook,
    unarchiveBook,
    unscheduleBook,
    updateBook,
};

export default booksService;
